import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Icon } from '../../ui/index.js';
import { cn } from '../../lib/cn.js';
import { EncryptedImage } from './EncryptedImage.js';
import { MediaViewer, type MediaItem } from './MediaViewer.js';
import { mediaKindOf } from './mediaType.js';

interface Props {
  items: MediaItem[];
  albumKey: CryptoKey;
  /** Owner-mode hook: when set, the fullscreen viewer shows a delete button
   *  in its header. Omitted in viewer-mode so receivers of a share can never
   *  delete from someone else's bucket. */
  onDelete?: (id: string) => Promise<void> | void;
  /** Owner-mode rating hook: when set, the fullscreen viewer renders editable
   *  stars; otherwise stars are read-only (or hidden if unrated). */
  onRate?: (id: string, rating: number) => Promise<void> | void;
  /** Bulk delete callback. When provided, multi-select is enabled and the
   *  bottom action sheet shows an "Elimina" affordance. */
  onBulkDelete?: (ids: string[]) => Promise<void> | void;
  /** Bulk move callback. When provided, the action sheet shows "Sposta". */
  onBulkMove?: (ids: string[]) => Promise<void> | void;
  /** Bulk tag callback. When provided, the action sheet shows "Tag" and the
   *  parent is responsible for opening the bulk-tag dialog. */
  onBulkTag?: (ids: string[]) => Promise<void> | void;
  /** Owner-mode hook to open the per-photo tag picker, surfaced from the
   *  fullscreen viewer header. Omitted in viewer-mode. */
  onEditTags?: (id: string) => void;
}

const LONG_PRESS_MS = 350;
const ROW_OVERSCAN = 3;

// Mirror of the Tailwind responsive grid we used to render. We need exact
// numeric values to size virtualized rows. Keep these aligned with the
// breakpoints applied elsewhere in the design system.
const COLUMN_BREAKPOINTS = [
  { min: 1280, cols: 5, gap: 12 }, // xl
  { min: 1024, cols: 4, gap: 12 }, // lg
  { min: 640, cols: 3, gap: 12 }, // sm
  { min: 0, cols: 2, gap: 8 },
] as const;

function pickLayout(viewportWidth: number) {
  for (const b of COLUMN_BREAKPOINTS) {
    if (viewportWidth >= b.min) return b;
  }
  return COLUMN_BREAKPOINTS[COLUMN_BREAKPOINTS.length - 1]!;
}

// Grid of clickable thumbnails. In normal mode tap opens the fullscreen
// viewer. With at least one of `onBulkDelete`/`onBulkMove` provided, a
// "Seleziona" toggle appears in the toolbar and tap-to-select replaces
// tap-to-open until the user exits selection mode. Long-press also enters
// selection mode (Apple Photos pattern).
//
// Rendering strategy: full `items` stays in state (decrypt metadata + presigned
// URLs continue to stream in transparently), but only the rows currently in
// the viewport (plus a small overscan) are mounted in the DOM. This keeps the
// first paint fluid even on 500+ photo albums.
export function MediaGrid({
  items,
  albumKey,
  onDelete,
  onRate,
  onBulkDelete,
  onBulkMove,
  onBulkTag,
  onEditTags,
}: Props) {
  // Track which photo is open by id, not index. Two reasons:
  //  - The items array can shrink underneath us (e.g. TopRatedView removes a
  //    photo when the user clears its rating). An index would dangle, point
  //    at a different photo or `undefined`, and `MediaViewer` would render
  //    null without us ever resetting the open state — subsequent taps on
  //    other tiles wouldn't remount the viewer.
  //  - `key={openId}` further down forces a clean remount when the user
  //    opens a different photo, so MediaViewer's lazy `initialIndex`
  //    initializer always reflects the latest items array.
  const [openId, setOpenId] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const selectable = Boolean(onBulkDelete || onBulkMove || onBulkTag);

  // Drop selection state when items change underneath us (after a successful
  // delete/move the parent re-renders with a shorter list).
  useEffect(() => {
    setSelected((curr) => {
      const ids = new Set(items.map((it) => it.id));
      const next = new Set<string>();
      for (const id of curr) if (ids.has(id)) next.add(id);
      return next;
    });
  }, [items]);

  const enterSelect = useCallback((seedId?: string) => {
    setSelectMode(true);
    if (seedId) setSelected(new Set([seedId]));
  }, []);

  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelected(new Set());
  }, []);

  const toggle = useCallback((id: string) => {
    setSelected((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function handleTileTap(id: string, idx: number) {
    if (selectMode) {
      toggle(id);
      return;
    }
    // Don't open the fullscreen viewer until the presigned URL has arrived
    // for this tile — Stage assumes a non-null url at the call site.
    if (!items[idx]?.url) return;
    setOpenId(id);
  }

  // Drop the viewer if its photo disappeared from the gallery while open
  // (e.g. the parent removed the row after a rating clear in TopRatedView).
  // Without this the viewer would render null but openId would still be set,
  // and tapping another tile would no-op (state unchanged → no remount).
  useEffect(() => {
    if (openId !== null && !items.some((it) => it.id === openId)) {
      setOpenId(null);
    }
  }, [items, openId]);

  const allSelected = items.length > 0 && selected.size === items.length;
  function selectAllToggle() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((it) => it.id)));
  }

  async function handleDelete() {
    if (!onBulkDelete || selected.size === 0) return;
    setBusy(true);
    try {
      await onBulkDelete([...selected]);
      exitSelect();
    } finally {
      setBusy(false);
    }
  }
  async function handleMove() {
    if (!onBulkMove || selected.size === 0) return;
    setBusy(true);
    try {
      await onBulkMove([...selected]);
      exitSelect();
    } finally {
      setBusy(false);
    }
  }
  async function handleTag() {
    if (!onBulkTag || selected.size === 0) return;
    setBusy(true);
    try {
      await onBulkTag([...selected]);
      exitSelect();
    } finally {
      setBusy(false);
    }
  }

  // Virtualization plumbing. We measure the grid container to compute the
  // exact tile size, and the container's offset to align the window-scroll
  // virtualizer with the rest of the page. Both can change underneath us
  // (resize, upload card appearing above), so we observe the body too.
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    const updateBox = () => {
      setContainerWidth(el.clientWidth);
      setScrollMargin(el.getBoundingClientRect().top + window.scrollY);
    };
    const updateViewport = () => {
      setViewportWidth(window.innerWidth);
    };

    updateBox();
    updateViewport();

    const containerObserver = new ResizeObserver(updateBox);
    containerObserver.observe(el);
    // Body resize means content above the grid changed height — reflow the
    // scrollMargin so the virtualizer keeps placing rows correctly.
    const bodyObserver = new ResizeObserver(updateBox);
    bodyObserver.observe(document.body);
    window.addEventListener('resize', updateViewport);

    return () => {
      containerObserver.disconnect();
      bodyObserver.disconnect();
      window.removeEventListener('resize', updateViewport);
    };
  }, []);

  const layout = pickLayout(viewportWidth);
  const cols = layout.cols;
  const gap = layout.gap;
  const tileSize = containerWidth > 0 ? Math.floor((containerWidth - gap * (cols - 1)) / cols) : 0;
  const rowHeight = tileSize > 0 ? tileSize + gap : 0;
  const rowCount = Math.ceil(items.length / cols);

  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => rowHeight,
    overscan: ROW_OVERSCAN,
    scrollMargin,
  });

  return (
    <>
      {selectable ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-on-surface-variant">
            {selectMode
              ? `${selected.size} ${selected.size === 1 ? 'selezionato' : 'selezionati'} su ${items.length}`
              : `${items.length} ${items.length === 1 ? 'elemento' : 'elementi'}`}
          </p>
          <div className="flex items-center gap-2">
            {selectMode ? (
              <button
                type="button"
                onClick={selectAllToggle}
                className="rounded-full px-3 py-1.5 text-xs font-semibold text-on-surface-variant transition-colors hover:bg-surface-container-low"
              >
                {allSelected ? 'Deseleziona tutto' : 'Seleziona tutto'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={selectMode ? exitSelect : () => setSelectMode(true)}
              disabled={items.length === 0}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                selectMode
                  ? 'bg-surface-container-low text-on-surface hover:bg-surface-container'
                  : 'text-on-surface-variant hover:bg-surface-container-low',
                items.length === 0 && 'cursor-not-allowed opacity-50',
              )}
            >
              <Icon name={selectMode ? 'close' : 'check_circle'} className="text-base" />
              {selectMode ? 'Annulla' : 'Seleziona'}
            </button>
          </div>
        </div>
      ) : null}

      <div ref={parentRef} className="w-full">
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: virtualizer.getTotalSize(),
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const start = virtualRow.index * cols;
            const rowItems = items.slice(start, start + cols);
            return (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: tileSize,
                  transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  columnGap: gap,
                }}
              >
                {rowItems.map((item, idx) => {
                  const i = start + idx;
                  const isSelected = selected.has(item.id);
                  const ord = isSelected ? [...selected].indexOf(item.id) + 1 : 0;
                  return (
                    <Thumb
                      key={item.id}
                      item={item}
                      albumKey={albumKey}
                      selectMode={selectMode}
                      isSelected={isSelected}
                      ordinal={ord}
                      onTap={() => handleTileTap(item.id, i)}
                      {...(selectable ? { onLongPress: () => enterSelect(item.id) } : {})}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {openId !== null && items.some((it) => it.id === openId) ? (
        <MediaViewer
          key={openId}
          items={items}
          initialIndex={Math.max(
            0,
            items.findIndex((it) => it.id === openId),
          )}
          albumKey={albumKey}
          onClose={() => setOpenId(null)}
          {...(onDelete ? { onDelete } : {})}
          {...(onRate ? { onRate } : {})}
          {...(onEditTags ? { onEditTags } : {})}
        />
      ) : null}

      {selectable && selectMode && selected.size > 0 ? (
        <BottomActionSheet
          count={selected.size}
          {...(onBulkTag ? { onTag: handleTag } : {})}
          {...(onBulkMove ? { onMove: handleMove } : {})}
          {...(onBulkDelete ? { onDelete: handleDelete } : {})}
          onCancel={exitSelect}
          busy={busy}
        />
      ) : null}
    </>
  );
}

function Thumb({
  item,
  albumKey,
  selectMode,
  isSelected,
  ordinal,
  onTap,
  onLongPress,
}: {
  item: MediaItem;
  albumKey: CryptoKey;
  selectMode: boolean;
  isSelected: boolean;
  ordinal: number;
  onTap: () => void;
  onLongPress?: () => void;
}) {
  const isVideo = mediaKindOf(item.filename) === 'video';
  const longPressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);

  function startPress() {
    if (!onLongPress || selectMode) return;
    longPressFired.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      onLongPress();
    }, LONG_PRESS_MS);
  }
  function cancelPress() {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }
  function handleClick() {
    // Suppress the click that fires after a long-press on touch devices.
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    onTap();
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={startPress}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onPointerCancel={cancelPress}
      onContextMenu={(e) => {
        if (selectMode || onLongPress) e.preventDefault();
      }}
      className={cn(
        'group relative h-full w-full overflow-hidden rounded-xl border bg-surface-container-low transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan/70 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        isSelected
          ? 'border-cyan ring-2 ring-cyan/40'
          : 'border-outline hover:border-on-surface-variant/40 hover:shadow-elevated',
      )}
      aria-label={item.filename}
      aria-pressed={selectMode ? isSelected : undefined}
    >
      <EncryptedImage
        id={item.id}
        url={item.url}
        albumKey={albumKey}
        alt={item.filename}
        {...(item.mimeType ? { mimeType: item.mimeType } : {})}
        className={cn(
          'absolute inset-0 h-full w-full object-cover transition-all duration-500',
          isSelected ? 'scale-95' : 'group-hover:scale-[1.04]',
        )}
      />
      <span
        className={cn(
          'pointer-events-none absolute inset-0 transition-opacity',
          isSelected
            ? 'bg-navy/40 opacity-100'
            : 'bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100',
        )}
      />
      {isVideo ? (
        <span className="absolute inset-0 flex items-center justify-center text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
          <Icon name="play_circle" className="text-4xl" filled />
        </span>
      ) : null}

      {selectMode ? (
        <span
          className={cn(
            'pointer-events-none absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold shadow-soft',
            isSelected
              ? 'bg-cyan text-navy'
              : 'bg-black/35 text-white ring-2 ring-white/70 backdrop-blur-sm',
          )}
        >
          {isSelected ? ordinal : ''}
        </span>
      ) : null}
    </button>
  );
}

interface BottomActionSheetProps {
  count: number;
  onTag?: () => Promise<void> | void;
  onMove?: () => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onCancel: () => void;
  busy: boolean;
}

function BottomActionSheet({
  count,
  onTag,
  onMove,
  onDelete,
  onCancel,
  busy,
}: BottomActionSheetProps) {
  // Position: fixed bottom, full-width on mobile, centered card on desktop.
  // Designed to feel like an iOS-style action bar that floats above the
  // grid, not a docked toolbar.
  return (
    <div
      role="dialog"
      aria-label={`${count} elementi selezionati`}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
    >
      <div className="pointer-events-auto flex w-full max-w-2xl items-center justify-between gap-3 rounded-2xl border border-outline bg-surface-container shadow-elevated backdrop-blur-md">
        <div className="flex items-center gap-3 pl-5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan text-navy">
            <span className="text-sm font-bold">{count}</span>
          </span>
          <span className="text-sm text-on-surface">
            {count === 1 ? 'selezionato' : 'selezionati'}
          </span>
        </div>
        <div className="flex items-center gap-1 pr-2">
          {onTag ? <SheetAction icon="sell" label="Tag" onClick={onTag} disabled={busy} /> : null}
          {onMove ? (
            <SheetAction icon="drive_file_move" label="Sposta" onClick={onMove} disabled={busy} />
          ) : null}
          {onDelete ? (
            <SheetAction
              icon="delete"
              label="Elimina"
              onClick={onDelete}
              disabled={busy}
              destructive
            />
          ) : null}
          <SheetAction icon="close" label="Annulla" onClick={onCancel} disabled={busy} ghost />
        </div>
      </div>
    </div>
  );
}

function SheetAction({
  icon,
  label,
  onClick,
  disabled,
  destructive,
  ghost,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  ghost?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50',
        destructive
          ? 'text-error hover:bg-error/10'
          : ghost
            ? 'text-on-surface-variant hover:bg-surface-container-low'
            : 'text-cyan hover:bg-cyan/10',
      )}
    >
      <Icon name={icon} className="text-base" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
